import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminUserApi from "../Api/AdminUserApi";
import RoleApi from "../Api/RoleApi";
import usePermissions from "../hook/usePermissions";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import { toast } from "react-toastify";

const AdminUserFormLayer = () => {
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        phoneNumber: "",
        email: "",
        pin: "",
        roleId: "",
        isActive: 1,
    });

    const [roleOptions, setRoleOptions] = useState([]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchRoles();
        if (isEdit) {
            getAdminUserById(id);
        }
    }, [isEdit, id]);

    const fetchRoles = async () => {
        const response = await RoleApi.getRoles({ limit: 100 });
        if (response && response.status && response.response.data) {
            const roles = response.response.data.map((role) => ({
                value: role._id,
                label: role.name,
            }));
            setRoleOptions(roles);
        }
    };

    const getAdminUserById = async (id) => {
        setLoading(true);
        const response = await AdminUserApi.getAdminUserById(id);
        if (response && response.status && response.response.data) {
            const data = response.response.data;
            setFormData({
                name: data.name || "",
                phoneNumber: data.phoneNumber || "",
                email: data.email || "",
                pin: "", // PIN is not fetched for security
                roleId: data.roleId?._id || data.roleId || "",
                isActive: data.isActive ?? 1,
            });
        }
        setLoading(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "phoneNumber") {
            const cleaned = value.replace(/\D/g, "").slice(0, 10);
            setFormData((prev) => ({ ...prev, [name]: cleaned }));
        } else if (name === "pin") {
            const cleaned = value.replace(/\D/g, "").slice(0, 6);
            setFormData((prev) => ({ ...prev, [name]: cleaned }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSelectChange = (selectedOption) => {
        setFormData((prev) => ({ ...prev, roleId: selectedOption?.value || "" }));
        if (errors.roleId) setErrors(prev => ({ ...prev, roleId: "" }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required";
        if (formData.phoneNumber.length < 10) newErrors.phoneNumber = "Invalid phone number";
        
        if (!isEdit) {
            if (!formData.email.trim()) newErrors.email = "Email is required";
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Invalid email format";
            if (!formData.pin.trim() || formData.pin.length < 4) newErrors.pin = "PIN (4-6 digits) is required";
        }
        
        if (!formData.roleId) newErrors.roleId = "Role is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            let response;
            if (isEdit) {
                const payload = {
                    name: formData.name,
                    phoneNumber: formData.phoneNumber,
                    roleId: formData.roleId,
                    isActive: formData.isActive
                };
                response = await AdminUserApi.updateAdminUser(id, payload);
            } else {
                const payload = { ...formData };
                response = await AdminUserApi.createAdminUser(payload);
            }

            if (response && response.status) {
                navigate("/admin-registration");
            }
        } catch (error) {
            console.error("API Error:", error);
            toast.error("An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (loading && isEdit && !formData.name) {
        return <div className="p-24 text-center">Loading user details...</div>;
    }

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-primary-600 pb-2 mb-0" style={{ color: '#002B5B' }}>
                    {isEdit ? "Edit Admin User" : "Create New Admin User"}
                </h6>
            </div>
            <div className="card-body p-24">
                <form onSubmit={handleSubmit}>
                    <div className="row gy-4">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Full Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors.name ? 'is-invalid' : ''}`}
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter Full Name"
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Email Address {!isEdit && <span className="text-danger">*</span>}</label>
                            <input
                                type="email"
                                className={`form-control radius-8 ${errors.email ? 'is-invalid' : ''}`}
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter Email Address"
                                disabled={isEdit}
                            />
                            {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Phone Number <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors.phoneNumber ? 'is-invalid' : ''}`}
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                placeholder="Enter 10-digit Phone Number"
                                maxLength={10}
                            />
                            {errors.phoneNumber && <div className="invalid-feedback">{errors.phoneNumber}</div>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Select Role <span className="text-danger">*</span></label>
                            <Select
                                options={roleOptions}
                                value={roleOptions.find((opt) => opt.value === formData.roleId)}
                                onChange={handleSelectChange}
                                styles={selectStyles(!!errors.roleId)}
                                placeholder="-- Select Role --"
                            />
                            {errors.roleId && <div className="text-danger mt-1" style={{ fontSize: '12px' }}>{errors.roleId}</div>}
                        </div>

                        {!isEdit && (
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-secondary-light">Login PIN <span className="text-danger">*</span></label>
                                <input
                                    type="password"
                                    className={`form-control radius-8 ${errors.pin ? 'is-invalid' : ''}`}
                                    name="pin"
                                    value={formData.pin}
                                    onChange={handleChange}
                                    placeholder="Enter 4-6 digit secure PIN"
                                    maxLength={6}
                                />
                                {errors.pin && <div className="invalid-feedback">{errors.pin}</div>}
                            </div>
                        )}

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Status</label>
                            <select
                                className="form-select radius-8"
                                name="isActive"
                                value={formData.isActive}
                                onChange={(e) => setFormData({ ...formData, isActive: Number(e.target.value) })}
                            >
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-3 mt-40">
                        <Link
                            to="/admin-registration"
                            className="btn btn-outline-danger-600 px-32 radius-8 d-flex align-items-center justify-content-center"
                            style={{ width: "130px", height: "48px" }}
                        >
                            Cancel
                        </Link>
                        {hasPermission("Admin Registration", isEdit ? "edit" : "add") && (
                            <button
                                type="submit"
                                className="btn btn-primary radius-8 px-20 py-11 d-flex align-items-center justify-content-center"
                                disabled={loading}
                                style={{ backgroundColor: "#D81B60", borderColor: "#D81B60", width: "130px", height: "48px" }}
                            >
                                {loading ? "Saving..." : isEdit ? "Update User" : "Create User"}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminUserFormLayer;
